import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ChevronRight, KeyRound, Loader2, Lock, Plus, Trash2, Variable, FlaskConical, Globe } from "lucide-react";
import { toast } from "sonner";
import { EnvironmentBadge, ProductionWarningBanner } from "@/components/EnvironmentBadge";
import { MetricCard, MetricGrid } from "@/components/MetricCard";
import { useConfirm } from "@/components/ConfirmDialog";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { formatDateTime } from "@/lib/datetime";
import { api, formatApiError } from "@/lib/api";

const TABS = [
  { id: "overview", label: "Overview", icon: Globe },
  { id: "variables", label: "Variables", icon: Variable },
  { id: "secrets", label: "Secrets", icon: Lock },
  { id: "logins", label: "Login States", icon: KeyRound },
];

function VariableDialog({ open, onOpenChange, envId, initial, onSaved }) {
  const isEdit = !!initial;
  const [key, setKey] = useState(initial?.key || "");
  const [value, setValue] = useState(initial?.value || "");
  const handleSave = async () => {
    if (!key.match(/^[A-Z][A-Z0-9_]*$/)) return toast.error("Key must be UPPER_SNAKE_CASE");
    try {
      await api.put(`/environments/${envId}/variables/${key}`, { key, value });
      toast.success(isEdit ? "Variable updated" : "Variable added");
      onSaved?.();
      onOpenChange(false);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[420px] rounded-xl border-[#E5E7EB]" data-testid="var-dialog">
        <DialogHeader>
          <DialogTitle className="font-['Geist'] font-semibold tracking-tight">{isEdit ? "Edit variable" : "Add variable"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-[#475569]">Key</label>
            <Input value={key} onChange={(e) => setKey(e.target.value.toUpperCase())} placeholder="BASE_URL, COMPANY_CODE" disabled={isEdit} data-testid="var-key-input" className="rounded-md border-[#E5E7EB] font-mono text-sm" />
            <p className="text-[10px] text-[#94A3B8]">Reference in tests as <code>{`{{${key || "VAR_NAME"}}}`}</code></p>
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-[#475569]">Value</label>
            <Input value={value} onChange={(e) => setValue(e.target.value)} placeholder="" data-testid="var-value-input" className="rounded-md border-[#E5E7EB]" />
          </div>
        </div>
        <DialogFooter>
          <button onClick={() => onOpenChange(false)} className="h-9 px-4 text-sm font-medium border border-[#E5E7EB] rounded-md bg-white hover:bg-[#F8FAFC]">Cancel</button>
          <button onClick={handleSave} disabled={!key} data-testid="var-save-button" className="h-9 px-4 text-sm font-medium rounded-md bg-[#3B82F6] text-white hover:bg-[#2563EB] disabled:opacity-60">Save</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SecretDialog({ open, onOpenChange, envId, initial, onSaved }) {
  const isEdit = !!initial; // "edit" means rotate the secret
  const [key, setKey] = useState(initial?.key || "");
  const [value, setValue] = useState("");
  const handleSave = async () => {
    if (!key.match(/^[A-Z][A-Z0-9_]*$/)) return toast.error("Key must be UPPER_SNAKE_CASE");
    if (!value) return toast.error("Value is required");
    try {
      await api.put(`/environments/${envId}/secrets/${key}`, { key, value });
      toast.success(isEdit ? "Secret rotated" : "Secret added");
      onSaved?.();
      onOpenChange(false);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[420px] rounded-xl border-[#E5E7EB]" data-testid="secret-dialog">
        <DialogHeader>
          <DialogTitle className="font-['Geist'] font-semibold tracking-tight">{isEdit ? `Rotate secret · ${initial.key}` : "Add secret"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          {!isEdit && (
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-[#475569]">Key</label>
              <Input value={key} onChange={(e) => setKey(e.target.value.toUpperCase())} placeholder="PASSWORD, API_KEY" data-testid="secret-key-input" className="rounded-md border-[#E5E7EB] font-mono text-sm" />
            </div>
          )}
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-[#475569]">Value (encrypted at rest)</label>
            <Input type="password" value={value} onChange={(e) => setValue(e.target.value)} placeholder="••••••" data-testid="secret-value-input" className="rounded-md border-[#E5E7EB] font-mono" autoComplete="new-password" />
            <p className="text-[10px] text-[#94A3B8]">Reference in tests as <code>{`{{${key || "SECRET_KEY"}}}`}</code></p>
          </div>
        </div>
        <DialogFooter>
          <button onClick={() => onOpenChange(false)} className="h-9 px-4 text-sm font-medium border border-[#E5E7EB] rounded-md bg-white hover:bg-[#F8FAFC]">Cancel</button>
          <button onClick={handleSave} data-testid="secret-save-button" className="h-9 px-4 text-sm font-medium rounded-md bg-[#3B82F6] text-white hover:bg-[#2563EB]">{isEdit ? "Rotate" : "Save"}</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function EnvironmentDetailPage() {
  const { projectId, envId } = useParams();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const [env, setEnv] = useState(null);
  const [tab, setTab] = useState("overview");
  const [variables, setVariables] = useState([]);
  const [secrets, setSecrets] = useState([]);
  const [logins, setLogins] = useState([]);

  const [varOpen, setVarOpen] = useState(false);
  const [editingVar, setEditingVar] = useState(null);
  const [secretOpen, setSecretOpen] = useState(false);
  const [editingSecret, setEditingSecret] = useState(null);

  const load = useCallback(() => {
    api.get(`/environments/${envId}`)
      .then((r) => setEnv(r.data))
      .catch((e) => { toast.error(formatApiError(e)); navigate(`/projects/${projectId}/environments`); });
    api.get(`/environments/${envId}/variables`).then((r) => setVariables(r.data.items || [])).catch(() => {});
    api.get(`/environments/${envId}/secrets`).then((r) => setSecrets(r.data.items || [])).catch(() => {});
    api.get(`/environments/${envId}/auth-states`).then((r) => setLogins(r.data || [])).catch(() => {});
  }, [envId, projectId, navigate]);

  useEffect(load, [load]);

  const handleDeleteVar = async (key) => {
    const ok = await confirm({ title: `Delete ${key}?`, danger: true });
    if (!ok) return;
    try { await api.delete(`/environments/${envId}/variables/${key}`); toast.success("Variable removed"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const handleDeleteSecret = async (key) => {
    const ok = await confirm({ title: `Delete secret ${key}?`, description: "Tests referencing this key will fail until it's re-added.", danger: true });
    if (!ok) return;
    try { await api.delete(`/environments/${envId}/secrets/${key}`); toast.success("Secret removed"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  if (!env) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-8 sm:py-10 text-sm text-[#64748B] flex items-center gap-2">
        <Loader2 size={14} className="animate-spin" /> Loading environment…
      </div>
    );
  }

  const isProd = env.type === "production";

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-8 py-8 sm:py-10" data-testid="environment-detail-page">
      <div className="flex items-center gap-1.5 text-xs text-[#64748B] mb-3">
        <button onClick={() => navigate("/projects")} className="hover:text-[#3B82F6]">Projects</button>
        <ChevronRight size={12} />
        <button onClick={() => navigate(`/projects/${projectId}/environments`)} className="hover:text-[#3B82F6]">Environments</button>
        <ChevronRight size={12} />
        <span className="text-[#0F172A] font-medium truncate">{env.name}</span>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="font-['Geist'] font-semibold text-2xl sm:text-[28px] tracking-tight text-[#0F172A] leading-tight">{env.name}</h1>
            <EnvironmentBadge type={env.type} size="lg" testId="env-detail-type-badge" />
          </div>
          {env.description && <p className="mt-1.5 text-sm text-[#64748B] max-w-2xl">{env.description}</p>}
          <p className="mt-2 text-xs font-mono text-[#64748B]">{env.base_url}</p>
        </div>
      </div>

      {isProd && <ProductionWarningBanner />}

      <div className="border-b border-[#E5E7EB] mb-6 overflow-x-auto">
        <nav className="flex gap-1" data-testid="env-tabs">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                data-testid={`env-tab-${t.id}`}
                className={`inline-flex items-center gap-1.5 px-3 h-10 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${active ? "border-[#3B82F6] text-[#0F172A]" : "border-transparent text-[#64748B] hover:text-[#0F172A]"}`}
              >
                <Icon size={14} />
                {t.label}
              </button>
            );
          })}
        </nav>
      </div>

      {tab === "overview" && (
        <div data-testid="env-overview-tab">
          <MetricGrid columns={4}>
            <MetricCard label="Variables" icon={Variable} value={env.variables.length} testId="metric-variables" />
            <MetricCard label="Secrets" icon={Lock} value={env.secret_keys.length} testId="metric-secrets" />
            <MetricCard label="Tests using this env" icon={FlaskConical} value={env.test_count} testId="metric-tests" />
            <MetricCard label="Login states" icon={KeyRound} value={env.login_state_count} testId="metric-logins" />
          </MetricGrid>

          <div className="surface-card p-5 mb-5">
            <h3 className="font-['Geist'] font-semibold text-sm text-[#0F172A] mb-3">Settings</h3>
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-y-3 gap-x-6 text-sm">
              <div className="flex items-center justify-between"><dt className="text-[#64748B]">Default browser</dt><dd className="font-medium text-[#0F172A] capitalize">{env.default_browser}</dd></div>
              <div className="flex items-center justify-between"><dt className="text-[#64748B]">Safety mode</dt><dd className="font-medium text-[#0F172A]">{env.safety_mode.replace(/_/g, " ")}</dd></div>
              <div className="flex items-center justify-between"><dt className="text-[#64748B]">Active</dt><dd className="font-medium text-[#0F172A]">{env.active ? "Yes" : "No"}</dd></div>
              <div className="flex items-center justify-between"><dt className="text-[#64748B]">Created</dt><dd className="font-medium text-[#0F172A]">{formatDateTime(env.created_at)}</dd></div>
            </dl>
          </div>
        </div>
      )}

      {tab === "variables" && (
        <div data-testid="env-variables-tab">
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-[#64748B]">Plain key-value pairs available to every test in this environment.</p>
            <button onClick={() => { setEditingVar(null); setVarOpen(true); }} data-testid="add-variable-button" className="inline-flex items-center gap-1.5 px-3 h-9 rounded-md bg-[#3B82F6] text-white text-sm font-medium hover:bg-[#2563EB]"><Plus size={13} />Add variable</button>
          </div>
          {variables.length === 0 ? (
            <div className="surface-card p-12 text-center" data-testid="empty-variables">
              <Variable size={24} className="mx-auto mb-3 text-[#94A3B8]" />
              <p className="text-sm text-[#64748B]">No variables yet — add <code>BASE_URL</code>, <code>COMPANY_CODE</code> etc.</p>
            </div>
          ) : (
            <div className="surface-card overflow-hidden">
              <table className="w-full">
                <thead><tr className="text-left bg-[#F8FAFC] dark:bg-[#0F172A]"><th className="px-4 py-2.5 text-xs font-medium text-[#475569]">Key</th><th className="px-4 py-2.5 text-xs font-medium text-[#475569]">Value</th><th className="px-4 py-2.5 w-32" /></tr></thead>
                <tbody className="divide-y divide-[#F1F5F9]">
                  {variables.map((v) => (
                    <tr key={v.key} data-testid={`variable-row-${v.key}`}>
                      <td className="px-4 py-3 font-mono text-sm text-[#0F172A]">{v.key}</td>
                      <td className="px-4 py-3 text-sm text-[#475569] font-mono truncate max-w-md">{v.value}</td>
                      <td className="px-4 py-3 text-right">
                        <button onClick={() => { setEditingVar(v); setVarOpen(true); }} data-testid={`edit-variable-${v.key}`} className="text-xs text-[#3B82F6] hover:underline mr-3">Edit</button>
                        <button onClick={() => handleDeleteVar(v.key)} data-testid={`delete-variable-${v.key}`} className="text-xs text-[#B91C1C] hover:underline">Delete</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {tab === "secrets" && (
        <div data-testid="env-secrets-tab">
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-[#64748B]">Secrets are encrypted at rest. Values are never returned by the API — only the key names.</p>
            <button onClick={() => { setEditingSecret(null); setSecretOpen(true); }} data-testid="add-secret-button" className="inline-flex items-center gap-1.5 px-3 h-9 rounded-md bg-[#3B82F6] text-white text-sm font-medium hover:bg-[#2563EB]"><Plus size={13} />Add secret</button>
          </div>
          {secrets.length === 0 ? (
            <div className="surface-card p-12 text-center" data-testid="empty-secrets">
              <Lock size={24} className="mx-auto mb-3 text-[#94A3B8]" />
              <p className="text-sm text-[#64748B]">No secrets yet — add <code>PASSWORD</code>, <code>API_KEY</code> etc.</p>
            </div>
          ) : (
            <div className="surface-card overflow-hidden">
              <table className="w-full">
                <thead><tr className="text-left bg-[#F8FAFC] dark:bg-[#0F172A]"><th className="px-4 py-2.5 text-xs font-medium text-[#475569]">Key</th><th className="px-4 py-2.5 text-xs font-medium text-[#475569]">Value</th><th className="px-4 py-2.5 text-xs font-medium text-[#475569]">Updated</th><th className="px-4 py-2.5 w-40" /></tr></thead>
                <tbody className="divide-y divide-[#F1F5F9]">
                  {secrets.map((s) => (
                    <tr key={s.key} data-testid={`secret-row-${s.key}`}>
                      <td className="px-4 py-3 font-mono text-sm text-[#0F172A]">{s.key}</td>
                      <td className="px-4 py-3 text-sm text-[#475569] font-mono">{s.masked_value}</td>
                      <td className="px-4 py-3 text-xs text-[#64748B]">{formatDateTime(s.updated_at)}</td>
                      <td className="px-4 py-3 text-right">
                        <button onClick={() => { setEditingSecret(s); setSecretOpen(true); }} data-testid={`rotate-secret-${s.key}`} className="text-xs text-[#3B82F6] hover:underline mr-3">Rotate</button>
                        <button onClick={() => handleDeleteSecret(s.key)} data-testid={`delete-secret-${s.key}`} className="text-xs text-[#B91C1C] hover:underline">Delete</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {tab === "logins" && (
        <div data-testid="env-logins-tab">
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-[#64748B]">Reusable browser sessions captured against this environment. Tests can attach to one.</p>
            <Link to="/auth-states" data-testid="capture-login-link" className="inline-flex items-center gap-1.5 px-3 h-9 rounded-md bg-[#3B82F6] text-white text-sm font-medium hover:bg-[#2563EB]"><Plus size={13} />Capture {env.name} login</Link>
          </div>
          {logins.length === 0 ? (
            <div className="surface-card p-12 text-center" data-testid="empty-logins">
              <KeyRound size={24} className="mx-auto mb-3 text-[#94A3B8]" />
              <p className="text-sm text-[#64748B]">No login states captured yet for this environment.</p>
            </div>
          ) : (
            <div className="surface-card overflow-hidden">
              <table className="w-full">
                <thead><tr className="text-left bg-[#F8FAFC] dark:bg-[#0F172A]"><th className="px-4 py-2.5 text-xs font-medium text-[#475569]">Login state</th><th className="px-4 py-2.5 text-xs font-medium text-[#475569]">Role</th><th className="px-4 py-2.5 text-xs font-medium text-[#475569]">Status</th><th className="px-4 py-2.5 text-xs font-medium text-[#475569]">Created</th><th className="px-4 py-2.5 text-xs font-medium text-[#475569]">Last used</th></tr></thead>
                <tbody className="divide-y divide-[#F1F5F9]">
                  {logins.map((l) => (
                    <tr key={l.id} data-testid={`login-row-${l.id}`}>
                      <td className="px-4 py-3 text-sm font-medium text-[#0F172A]">{l.name}</td>
                      <td className="px-4 py-3 text-xs text-[#475569] capitalize">{l.role || "—"}</td>
                      <td className="px-4 py-3"><span className={`inline-flex px-2 py-0.5 rounded text-[10px] font-medium uppercase tracking-wider ${l.status === "active" ? "bg-[#DCFCE7] text-[#065F46]" : l.status === "expired" ? "bg-[#FEF3C7] text-[#92400E]" : "bg-[#FEE2E2] text-[#991B1B]"}`}>{l.status || "active"}</span></td>
                      <td className="px-4 py-3 text-xs text-[#64748B]">{formatDateTime(l.created_at)}</td>
                      <td className="px-4 py-3 text-xs text-[#64748B]">{l.last_used_at ? formatDateTime(l.last_used_at) : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      <VariableDialog key={`var-${editingVar?.key || "new"}-${varOpen}`} open={varOpen} onOpenChange={setVarOpen} envId={envId} initial={editingVar} onSaved={load} />
      <SecretDialog key={`sec-${editingSecret?.key || "new"}-${secretOpen}`} open={secretOpen} onOpenChange={setSecretOpen} envId={envId} initial={editingSecret} onSaved={load} />
    </div>
  );
}
